var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { OTTO_PROFILE_STEPS, PROFILE_STEPS, PURCHASING_MOTIVATION } from '@marketing/profile/enums';
import { calculateNumBundles } from '@utils/common_utils';
import { ADD_ON_PROBIOTICS_PRODUCT_TYPES, FREE_PRODUCT_DISPLAY_NAMES, FUNNEL_VERSIONS, MAX_NUM_RECIPES_FOR_FLEXIBLE_FOOD_SIGNUP, PET_TYPE, TREATS_PRODUCT_TYPES, } from '@variables';
var PROFILE_VERSION_TO_BASE_PATH = {
    juicy: '/profile',
    bab: '/profile/bab',
    otto: '/profile/otto',
};
/**
 * If profile has address, they should be able to reach the summary page.
 * If they customer name and email, they should be able to reach the address page.
 * If all pets have recipe, they should be able to reach the contact page.
 * Otherwise, they likely have pets with missing profile info,
 *    so return the current step percentage.
 */
var determineProfileProgressPercentage = function (currentStepPercentage, profileData) {
    if (isOneTimeSamplerPackTrial(profileData.version)) {
        if (profileData.state && profileData.zipcode) {
            return OTTO_PROFILE_STEPS.summaryForm.progressPercentage;
        }
        if (profileData.customerName && profileData.email) {
            return OTTO_PROFILE_STEPS.addressForm.progressPercentage;
        }
        return OTTO_PROFILE_STEPS.petAndCustomerForm.progressPercentage;
    }
    if (profileData.pets.length >= 1) {
        if (profileData.state && profileData.zipcode) {
            return PROFILE_STEPS.summaryForm.progressPercentage;
        }
        if (profileData.customerName && profileData.email) {
            return PROFILE_STEPS.addressForm.progressPercentage;
        }
        if (noPetsMissingProperty(profileData.pets, 'recipe')) {
            return PROFILE_STEPS.contactForm.progressPercentage;
        }
    }
    var pet = profileData.pets[0];
    if (pet) {
        if (pet.weight) {
            return PROFILE_STEPS.petMealsForm.progressPercentage;
        }
        if (pet.currentWeight) {
            return PROFILE_STEPS.petTargetWeightForm.progressPercentage;
        }
        if (pet.birthdayYear) {
            return PROFILE_STEPS.petCurrentWeightForm.progressPercentage;
        }
        if (pet.breed) {
            return PROFILE_STEPS.petAgeForm.progressPercentage;
        }
        if (pet.gender) {
            return PROFILE_STEPS.petBreedForm.progressPercentage;
        }
    }
    return currentStepPercentage;
};
var isValidPet = function (pet) {
    return Boolean(pet && pet.name && pet.currentWeight);
};
// Finds pets that have a missing property
var findPetWithoutProperty = function (pets, propertyName) {
    var incompletePetIndex;
    var incompletePetProfile = pets.find(function (pet, index) {
        if (isValidPet(pet) && !pet[propertyName]) {
            incompletePetIndex = index;
            return pet;
        }
    });
    return [incompletePetProfile, incompletePetIndex];
};
/**
 * Checks if all pets have a specific property set. Currently, used to check that
 * gender, mealPlanItems, and recipe are set to progress in the funnel. Otherwise, will
 * will redirect to the start to complete the profile.
 */
var noPetsMissingProperty = function (pets, propertyName) { return pets.every(function (pet) { return pet[propertyName]; }); };
var isAddedItem = function (orderItems, productId) {
    return orderItems.some(function (item) { return item.productType === productId; });
};
/**
 * Sets a list of recommended recipes based on selected purchasing motivations.
 * Default recommendation is chicken
 */
var getRecommendedRecipes = function (purchasingMotivations, maxRecipes, isOverweight) {
    if (isOverweight === void 0) { isOverweight = false; }
    var recipes = new Set();
    if (purchasingMotivations.includes('picky_eater')) {
        recipes.add('chicken');
    }
    if (purchasingMotivations.includes('coat_issues')) {
        recipes.add('turkey_chicken');
    }
    if (purchasingMotivations.includes('no_issues')) {
        recipes.add('chicken');
    }
    if (isOverweight) {
        recipes.add('turkey');
    }
    if (purchasingMotivations.includes('allergies')) {
        recipes.add('pork');
        recipes.add('lamb');
        recipes.delete('beef');
        recipes.delete('chicken');
        recipes.delete('turkey');
        recipes.delete('turkey_chicken');
    }
    // Restrict to a max of recipes to be selected.
    return recipes.size ? Array.from(recipes).slice(0, maxRecipes) : ['chicken'];
};
var getRecommendedProduct = function (orderItems, pet, petSurvey) {
    var hasFreeWafersInCart = isAddedItem(orderItems, TREATS_PRODUCT_TYPES.beefBlueberryTreats7ozFree) ||
        isAddedItem(orderItems, TREATS_PRODUCT_TYPES.chickenCranberryTreats7ozFree);
    if (pet.petType === PET_TYPE.DOG) {
        if (pet.currentWeight > pet.weight &&
            !isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum)) {
            return ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum;
        }
        if (petSurvey && petSurvey.purchasingMotivations) {
            if (petSurvey.purchasingMotivations.includes(PURCHASING_MOTIVATION.ALLERGIES)) {
                if (!isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted)) {
                    return ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted;
                }
            }
            if (petSurvey.purchasingMotivations.includes(PURCHASING_MOTIVATION.COAT_ISSUES) &&
                !isAddedItem(orderItems, ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum)) {
                return ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum;
            }
            if (petSurvey.purchasingMotivations.includes(PURCHASING_MOTIVATION.PICKY_EATER) &&
                !isAddedItem(orderItems, TREATS_PRODUCT_TYPES.chickenCranberryTreats7oz) &&
                !hasFreeWafersInCart) {
                return TREATS_PRODUCT_TYPES.chickenCranberryTreats7oz;
            }
            if (petSurvey.purchasingMotivations.includes(PURCHASING_MOTIVATION.NO_ISSUES) &&
                !isAddedItem(orderItems, TREATS_PRODUCT_TYPES.beefBlueberryTreats7oz) &&
                !hasFreeWafersInCart) {
                return TREATS_PRODUCT_TYPES.beefBlueberryTreats7oz;
            }
        }
    }
    return ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted;
};
var prioritizeProducts = function (products, recommendedProducts) {
    return __spreadArray([], products, true).sort(function (a, b) {
        var aIndex = recommendedProducts.indexOf(a.key.name);
        var bIndex = recommendedProducts.indexOf(b.key.name);
        var aIsRecommended = aIndex !== -1;
        var bIsRecommended = bIndex !== -1;
        if (aIsRecommended && bIsRecommended) {
            return aIndex - bIndex; // maintain recommended order
        }
        if (aIsRecommended)
            return -1;
        if (bIsRecommended)
            return 1;
        if (a.isNew && !b.isNew)
            return -1;
        if (!a.isNew && b.isNew)
            return 1;
        return 0;
    });
};
var getBackgroundColor = function (productKeyName) {
    switch (productKeyName) {
        case 'beef':
        case TREATS_PRODUCT_TYPES.beefJerkyTreats4oz:
            return 'bg-chew-toy';
        case 'lamb':
            return 'bg-chew-toy-900';
        case 'pork':
            return 'bg-fish';
        case ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogFullSpectrum:
            return 'bg-plate';
        case 'turkey':
        case 'turkey_chicken':
        case ADD_ON_PROBIOTICS_PRODUCT_TYPES.dogGITargeted:
            return 'bg-kale';
        case 'chicken':
        case TREATS_PRODUCT_TYPES.chickenJerkyTreats4oz:
            return 'bg-squash';
        case TREATS_PRODUCT_TYPES.sweetPotatoTreats6oz:
            return 'bg-squash';
        case TREATS_PRODUCT_TYPES.beefBlueberryTreats7oz:
            return 'bg-fish-700 ';
        case TREATS_PRODUCT_TYPES.chickenCranberryTreats7oz:
            return 'bg-red-apple-300';
        default:
            return '';
    }
};
var isJuicyProfile = function (version) {
    return version === FUNNEL_VERSIONS.PERSONALIZED;
};
var isFlexibleProfile = function (version) {
    return version === FUNNEL_VERSIONS.FLEXIBLE;
};
var isOneTimeSamplerPackTrial = function (version) {
    return version === FUNNEL_VERSIONS.OTTO;
};
var getMaxNumPets = function (version) {
    if (isOneTimeSamplerPackTrial(version)) {
        return 1;
    }
    return 6;
};
var getAddOnProduct = function (addOnProducts, productType) {
    return addOnProducts.find(function (product) { return product.key.name === productType; });
};
var calculateAddOnSavings = function (addOnProducts, orderItems) {
    var savings = 0;
    orderItems.length &&
        orderItems.forEach(function (item) {
            var product = getAddOnProduct(addOnProducts, item.productType);
            if (product.shopifyCostCents) {
                savings += (item.units * (product.shopifyCostCents - product.costCents)) / 100;
            }
        });
    return savings;
};
/* Validates that number of bundles is within expected range */
// Each pet can select up to the max number of recipes (4) and will get 1 bundle per recipe
var isValidNumBundles = function (numBundles, pets) {
    return numBundles > 0 && numBundles <= MAX_NUM_RECIPES_FOR_FLEXIBLE_FOOD_SIGNUP * pets.length;
};
/* Checks that current delivery interval and number of bundles is valid and all pets have recipes */
var isValidFixedSkuProfile = function (deliveryInterval, pets) {
    if (!deliveryInterval || !noPetsMissingProperty(pets, 'recipe')) {
        return false;
    }
    return isValidNumBundles(calculateNumBundles(pets), pets);
};
var isValidOneTimeTrialProfile = function (orderItems) {
    return orderItems && orderItems.length === 1;
};
/* Check if user has enough data to render a summary view (recipes needed for bab summary). */
var hasValidSummary = function (profileData) {
    var deliveryInterval = profileData.deliveryInterval, email = profileData.email, orderCosts = profileData.orderCosts, orderItems = profileData.orderItems, pets = profileData.pets, state = profileData.state, version = profileData.version, zipcode = profileData.zipcode;
    if (!pets || !pets.length || !noPetsMissingProperty(pets, 'mealPlanItems')) {
        return false;
    }
    if (!state || !zipcode) {
        return false;
    }
    if (isFlexibleProfile(version) && !isValidFixedSkuProfile(deliveryInterval, pets)) {
        return false;
    }
    if (isOneTimeSamplerPackTrial(version) && !isValidOneTimeTrialProfile(orderItems)) {
        return false;
    }
    // All subscription based profiles should have pets with recipes selected.
    if (!isOneTimeSamplerPackTrial(version) && !noPetsMissingProperty(pets, 'recipe')) {
        return false;
    }
    // All profiles should have a non-zero subtotal (i.e. cost of subscription food + order items).
    return Boolean(email && orderCosts && orderCosts.subtotal);
};
var getProfileBasePath = function (version) {
    return PROFILE_VERSION_TO_BASE_PATH[version] || PROFILE_VERSION_TO_BASE_PATH.juicy;
};
var determineNextStepLink = function (profileData) {
    var pets = profileData.pets, version = profileData.version, deliveryInterval = profileData.deliveryInterval;
    var basePath = getProfileBasePath(version);
    var _a = findPetWithoutProperty(pets, 'gender'), petMissingGender = _a[0], petMissingGenderIndex = _a[1];
    if (petMissingGender) {
        return {
            to: "".concat(basePath, "/pet/").concat(petMissingGenderIndex, "/gender"),
            text: "Complete ".concat(petMissingGender.name, "\u2019s profile"),
        };
    }
    var _b = findPetWithoutProperty(pets, 'currentWeight'), petMissingCurrentWeight = _b[0], petMissingCurrentWeightIndex = _b[1];
    if (petMissingCurrentWeight) {
        return {
            to: "".concat(basePath, "/pet/").concat(petMissingCurrentWeightIndex, "/gender"),
            text: "Complete ".concat(petMissingCurrentWeight.name, "\u2019s profile"),
        };
    }
    var _c = findPetWithoutProperty(pets, 'weight'), petMissingTargetWeight = _c[0], petMissingTargetWeightIndex = _c[1];
    if (petMissingTargetWeight) {
        return {
            to: "".concat(basePath, "/pet/").concat(petMissingTargetWeightIndex, "/gender"),
            text: "Complete ".concat(petMissingTargetWeight.name, "\u2019s profile"),
        };
    }
    var _d = findPetWithoutProperty(pets, 'recipe'), petMissingRecipe = _d[0], petMissingRecipeIndex = _d[1];
    if (petMissingRecipe) {
        return {
            to: "".concat(basePath, "/pet/").concat(petMissingRecipeIndex, "/meals"),
            text: "Choose ".concat(petMissingRecipe.name, "\u2019s recipe"),
        };
    }
    if (isFlexibleProfile(version)) {
        if (pets[0].mealPlanItems.length === 0 || !deliveryInterval) {
            return { to: "".concat(basePath, "/pet/0/meals"), text: 'Choose meals' };
        }
    }
    if (!profileData.customerName || !profileData.email) {
        return { to: "".concat(basePath, "/contact"), text: 'Add contact info' };
    }
    if (!profileData.state || !profileData.zipcode) {
        return { to: "".concat(basePath, "/address"), text: 'Add shipping address' };
    }
    return { to: "".concat(basePath, "/summary"), text: 'Add payment to start trial' };
};
var findProductTypeFromHashString = function (addOnProducts, productTypeFromHash) {
    // Ensure productType is valid and avoid adding free products
    var product = addOnProducts.find(function (addOnProduct) {
        return addOnProduct.key.name === productTypeFromHash &&
            !FREE_PRODUCT_DISPLAY_NAMES[productTypeFromHash];
    });
    return product ? product : null;
};
var findFreeGiftWithPurchaseItem = function (orderItems) {
    return orderItems.find(function (item) { return FREE_PRODUCT_DISPLAY_NAMES[item.productType]; });
};
export { calculateAddOnSavings, determineNextStepLink, determineProfileProgressPercentage, findFreeGiftWithPurchaseItem, findPetWithoutProperty, findProductTypeFromHashString, getBackgroundColor, getMaxNumPets, getProfileBasePath, getRecommendedProduct, getRecommendedRecipes, hasValidSummary, isFlexibleProfile, isJuicyProfile, isOneTimeSamplerPackTrial, isValidPet, noPetsMissingProperty, prioritizeProducts, };
